// Mock Service Worker for push notifications
// This would be replaced with actual service worker in production

// Lightweight debug logger wrapper to avoid direct console.* usage everywhere.
// Keeps a single console usage guarded by an eslint disable comment.
const logDebug = (...args: unknown[]) => {
  // eslint-disable-next-line no-console
  console.debug(...args);
};

export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!isNotificationSupported()) {
    logDebug('This browser does not support notifications');
    return 'denied';
  }

  try {
    const permission = await Notification.requestPermission();
    logDebug('Notification permission status:', permission);
    return permission;
  } catch (error) {
    logDebug('Error requesting notification permission:', error);
    return 'denied';
  }
};

export const sendPushNotification = (title: string, options: NotificationOptions) => {
  if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
    const notification = new Notification(title, {
      ...options,
      icon: '/favicon.ico',
      badge: '/badge-icon.png',
    });

    notification.onclick = (event) => {
      event.preventDefault();
      window.focus();
      notification.close();
    };

    // Auto-close after 10 seconds for non-critical notifications
    if (!options.requireInteraction) {
      setTimeout(() => notification.close(), 10000);
    }

    return notification;
  }
  return null;
};

export const playNotificationSound = (volume: number = 0.5) => {
  // Cross-browser AudioContext reference
  const win = window as unknown as {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };
  const AudioCtx = win.AudioContext ?? win.webkitAudioContext;
  if (!AudioCtx) {
    logDebug('Web Audio API is not supported in this browser');
    return;
  }

  try {
    const audioContext = new AudioCtx();

    // Create a simple beep sound
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.type = 'sine';
    oscillator.frequency.value = 800; // 800 Hz tone

    gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
    // Fade out to a small value over 0.5s to avoid clicks
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.start();

    // Stop the oscillator after 600ms and close the context
    setTimeout(() => {
      try {
        oscillator.stop();
      } catch {
        // ignore if already stopped
      }
      // close may not be supported in older implementations
      if (typeof audioContext.close === 'function') {
        audioContext.close().catch(() => {
          /* ignore close errors */
        });
      }
    }, 600);
  } catch (error) {
    logDebug('Failed to play notification sound:', error);
  }
};

// Check if notifications are supported
export const isNotificationSupported = (): boolean => {
  return typeof window !== 'undefined' && 'Notification' in window;
};

/**
 * Checks if push notifications can be sent based on permission and settings
 */
export const canSendPush = (pushEnabledFromSettings: boolean = true): boolean => {
  return (
    isNotificationSupported() && Notification.permission === 'granted' && pushEnabledFromSettings
  );
};

export const isServiceWorkerSupported = (): boolean => {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
};

export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!isServiceWorkerSupported()) {
    logDebug('Service Worker is not supported in this browser');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    logDebug('Service Worker registered successfully:', registration);
    return registration;
  } catch (error) {
    logDebug('Service Worker registration failed:', error);
    return null;
  }
};

// Utility function to format notification based on sensor data
export const formatSensorNotification = (
  sensor: string,
  value: number,
  threshold: number,
  condition: 'above' | 'below' | 'equals',
): {
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
} => {
  const sensorNames: { [key: string]: string } = {
    temperature: 'Temperature',
    humidity: 'Humidity',
    ph: 'pH Level',
    ec: 'EC Level',
    waterLevel: 'Water Level',
    lightIntensity: 'Light Intensity',
    nutrients: 'Nutrients',
    growthRate: 'Growth Rate',
  };

  const units: { [key: string]: string } = {
    temperature: '°C',
    humidity: '%',
    ph: '',
    ec: ' mS/cm',
    waterLevel: '%',
    lightIntensity: ' lux',
    nutrients: '%',
    growthRate: '%/day',
  };

  const sensorName = sensorNames[sensor] || sensor;
  const unit = units[sensor] || '';

  let priority: 'low' | 'medium' | 'high' | 'critical' = 'medium';
  let title = '';
  let message = '';

  // Determine priority and message based on sensor and condition
  if (sensor === 'ph') {
    if (condition === 'below' && threshold < 5.5) {
      priority = 'critical';
      title = 'Critical pH Alert!';
      message = `pH level is critically low at ${value}${unit}. Immediate action required to prevent crop damage.`;
    } else if (condition === 'above' && threshold > 7.5) {
      priority = 'critical';
      title = 'Critical pH Alert!';
      message = `pH level is critically high at ${value}${unit}. Immediate action required to prevent crop damage.`;
    } else {
      priority = 'high';
      title = 'pH Warning';
      message = `pH level is ${condition} ${threshold}${unit}. Current value: ${value}${unit}`;
    }
  } else if (sensor === 'temperature') {
    if (condition === 'above' && threshold > 30) {
      priority = 'critical';
      title = 'Critical Temperature Alert!';
      message = `Temperature is dangerously high at ${value}${unit}. Risk of plant heat stress.`;
    } else if (condition === 'below' && threshold < 15) {
      priority = 'high';
      title = 'Low Temperature Warning';
      message = `Temperature is too low at ${value}${unit}. Plants may be stressed.`;
    } else {
      priority = 'medium';
      title = 'Temperature Alert';
      message = `Temperature is ${condition} ${threshold}${unit}. Current value: ${value}${unit}`;
    }
  } else if (sensor === 'waterLevel') {
    if (condition === 'below' && threshold < 10) {
      priority = 'critical';
      title = 'Critical Water Level!';
      message = `Water level is critically low at ${value}${unit}. System may shut down soon.`;
    } else if (condition === 'below' && threshold < 25) {
      priority = 'high';
      title = 'Low Water Level';
      message = `Water level is low at ${value}${unit}. Refill recommended.`;
    } else {
      priority = 'medium';
      title = 'Water Level Alert';
      message = `Water level is ${condition} ${threshold}${unit}. Current value: ${value}${unit}`;
    }
  } else {
    // Generic sensor alert
    priority = 'medium';
    title = `${sensorName} Alert`;
    message = `${sensorName} is ${condition} ${threshold}${unit}. Current value: ${value}${unit}`;
  }

  return { title, message, priority };
};
